import { Title } from '@mantine/core'
import type { IDT } from '@seen/core/models/care-teams/idt'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import {
  createSDRSignatures,
  uncoveredSDRRoles,
} from '@seen/core/models/tasks/signature/createSDRSignatures'
import { SDRSignatureTask } from '@seen/core/models/tasks/signature/sdrSignatureTask'
import { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import { Signatures } from '#src/components/signature/Signatures.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useBatchMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function SDRSignatures(props: { sdr: SDR; careTeam: IDT }): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const practitionerRef = useCurrentPractitioner().reference

  const signaturesQuery = useSearch(
    SDRSignatureTask,
    SDRSignatureTask.bySDR(props.sdr.reference)
  )

  const createSignatureMutation = useBatchMutation()

  const handleCreate = (): void => {
    createSignatureMutation.mutate((batch) => {
      createSDRSignatures(batch, {
        sdr: props.sdr.reference,
        idt: props.careTeam,
        requester: practitionerRef,
      })
    })
  }

  return (
    <FhirQueryLoader query={signaturesQuery}>
      {(signatures) => {
        const needsSignatureRequest = uncoveredSDRRoles(signatures).length > 0

        return (
          <>
            <div className="flex items-center justify-between">
              <Title order={4}>
                Attestation
                {signatures.length > 0 && (
                  <> Required {SignatureTask.completedRatio(signatures)}</>
                )}
              </Title>
              {needsSignatureRequest && (
                <Button
                  onClick={handleCreate}
                  disabled={isReadOnly}
                  loading={createSignatureMutation.isPending}
                >
                  Request Signatures
                </Button>
              )}
            </div>
            {signatures.length > 0 && (
              <Signatures
                signatures={signatures}
                showDiscipline
                instructions="Please review and sign off on the decision of this SDR."
              />
            )}
          </>
        )
      }}
    </FhirQueryLoader>
  )
}
